"use client";

import { useEffect, useRef, useState } from "react";

import { isLikelyAnimatedGifUrl } from "@/shared/lib/image-utils";

// KAN-302: JS-чтение кросс-оригин GIF без CORS-заголовков блокируется CORB и
// сыплет warning'и в консоль. Пробуем конвертацию только там, где CORS
// гарантирован: same-origin и наши CDN. Внешние хосты (discord/twitch/google/…)
// отдаём как есть — GIF останется анимированным, без сломанной аватарки и шума.
function canReadCrossOrigin(src: string): boolean {
  try {
    const url = new URL(src, window.location.href);
    if (url.origin === window.location.origin) return true;
    return url.hostname.endsWith(".gltournament.ru");
  } catch {
    return false;
  }
}

export function useStaticGifObjectUrl(
  src: string | null | undefined,
  wantStatic: boolean,
): { url: string | null | undefined; pending: boolean } {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [failed, setFailed] = useState(false);
  const objectUrlRef = useRef<string | null>(null);

  const effectiveWant = Boolean(
    wantStatic &&
      src &&
      isLikelyAnimatedGifUrl(src) &&
      typeof window !== "undefined" &&
      canReadCrossOrigin(src),
  );

  useEffect(() => {
    if (!effectiveWant) {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
      setBlobUrl(null);
      setPending(false);
      setFailed(false);
      return;
    }

    let cancelled = false;
    setPending(true);
    setFailed(false);
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
    setBlobUrl(null);

    void (async () => {
      try {
        const res = await fetch(src!, { mode: "cors", credentials: "omit" });
        if (!res.ok) throw new Error("fetch");
        const blob = await res.blob();
        const bmp = await createImageBitmap(blob);
        const c = document.createElement("canvas");
        c.width = bmp.width;
        c.height = bmp.height;
        const ctx = c.getContext("2d");
        if (!ctx) throw new Error("2d");
        ctx.drawImage(bmp, 0, 0);
        bmp.close();
        const pngBlob = await new Promise<Blob | null>((resolve) =>
          c.toBlob((b) => resolve(b), "image/png"),
        );
        if (!pngBlob) throw new Error("toBlob");
        const created = URL.createObjectURL(pngBlob);
        if (cancelled) {
          URL.revokeObjectURL(created);
          return;
        }
        objectUrlRef.current = created;
        setBlobUrl(created);
        setPending(false);
      } catch {
        if (!cancelled) {
          setFailed(true);
          setPending(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };
  }, [src, effectiveWant]);

  if (!effectiveWant) return { url: src ?? undefined, pending: false };
  if (blobUrl) return { url: blobUrl, pending: false };
  if (pending) return { url: undefined, pending: true };
  if (failed) return { url: src ?? undefined, pending: false };
  return { url: undefined, pending: true };
}
